import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { provideTranslocoScope } from '@jsverse/transloco';
import { IsBasicProfileDirective, IsPremiumProfileDirective } from '@rma/public/generic/ui/feature-level-check';
import { ShareButtonComponent } from '@rma/public/generic/ui/share/ui-share-buttton';
import { SkeletonGridComponent } from '@rma/public/generic/ui/skeleton';
import { TrixelComponent, TrixelSourceCategories } from '@rma/public/generic/ui/trixels';
import { StaticImageUrlPipe } from '@rma/public/generic/util/environment';
import {
  AwardsStore,
  BadgesStore,
  GalleryStore,
  PremiumToggleStore,
  ProfileAttributesStore,
  ProfileAward,
  ProfileStore,
  ProfileSummary,
  ReviewsStore,
} from '@rma/public/profiles/private/domain';
import { ProfileExternalReviewsComponent } from '@rma/public/profiles/private/orc-external-reviews';
import { ProfileMapInlineComponent } from '@rma/public/profiles/private/orc-profile-map-inline';
import { ClaimProfileBannerComponent } from '@rma/public/profiles/private/ui-claim-profile-banner';
import { ProfileAboutComponent } from '@rma/public/profiles/private/ui-profile-about';
import { ProfileAwardsComponent } from '@rma/public/profiles/private/ui-profile-awards';
import { ProfileHeaderComponent } from '@rma/public/profiles/private/ui-profile-header';
import { ProfilePremiumToggleComponent } from '@rma/public/profiles/private/ui-profile-premium-toggle';
import { ProfileRecognitionComponent } from '@rma/public/profiles/private/ui-profile-recognition';
import { ProfileReviewsOverviewComponent } from '@rma/public/profiles/private/ui-profile-reviews';
import { ProfileUnclaimedProfileDisclaimerComponent } from '@rma/public/profiles/private/ui-unclaimed-profile-disclaimer';
import { ProfileMetaService, ProfileMetaSource } from '@rma/public/profiles/private/util-meta-service';
import {
  MortgageBrokerEnquiryTypes,
  type ProfileBadges,
  type ProfileContactAnalytics,
  type ProfileGalleryAssetWithThumbnail,
  type ReviewAttribute,
} from '@rma/public/profiles/public/domain';
import { ContactFormCtaComponent } from '@rma/public/profiles/public/orc-contact-form';
import { ProfileContactComponent } from '@rma/public/profiles/public/ui-profile-contact';
import type { ReviewList } from '@rma/public/reviews/public/domain';
import { combineLatest, map, Observable, of } from 'rxjs';

@Component({
  selector: 'rma-mortgage-broker-profile',
  imports: [
    AsyncPipe,
    ClaimProfileBannerComponent,
    ContactFormCtaComponent,
    IsBasicProfileDirective,
    IsPremiumProfileDirective,
    MatButtonModule,
    NgTemplateOutlet,
    ProfileAboutComponent,
    ProfileAwardsComponent,
    ProfileContactComponent,
    ProfileExternalReviewsComponent,
    ProfileHeaderComponent,
    ProfileMapInlineComponent,
    ProfilePremiumToggleComponent,
    ProfileRecognitionComponent,
    ProfileReviewsOverviewComponent,
    ProfileUnclaimedProfileDisclaimerComponent,
    ShareButtonComponent,
    SkeletonGridComponent,
    StaticImageUrlPipe,
    TrixelComponent,
  ],
  providers: [
    provideTranslocoScope('profileScope'),
    AwardsStore,
    BadgesStore,
    GalleryStore,
    PremiumToggleStore,
    ProfileStore,
    ProfileAttributesStore,
    ReviewsStore,
  ],
  templateUrl: './mortgage-broker-profile.component.html',
  host: {
    class: 'w-full py-6',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MortgageBrokerProfileComponent implements OnInit {
  private readonly profileMetaService = inject(ProfileMetaService);
  private readonly awardsStore = inject(AwardsStore);
  private readonly badgesStore = inject(BadgesStore);
  private readonly profileStore = inject(ProfileStore);
  private readonly premiumToggleStore = inject(PremiumToggleStore);
  private readonly reviewsStore = inject(ReviewsStore);
  private readonly galleryStore = inject(GalleryStore);
  private readonly profileAttributeStore = inject(ProfileAttributesStore);

  public readonly awards$: Observable<ProfileAward[]>;
  public readonly badges$: Observable<ProfileBadges[]>;
  public readonly contactUrl$: Observable<string | undefined>;
  public readonly contactAnalytics$: Observable<ProfileContactAnalytics>;
  public readonly downgrade$: Observable<boolean>;
  public readonly galleryAssets$: Observable<ProfileGalleryAssetWithThumbnail[]>;
  public readonly hasTrial$: Observable<boolean>;
  public readonly premiumOverride$: Observable<boolean>;
  public readonly profile$: Observable<ProfileSummary>;
  public readonly reviews$: Observable<ReviewList>;
  public readonly share$: Observable<ShareData>;
  public readonly showClaim$: Observable<boolean>;
  public readonly showMapCta$: Observable<boolean>;
  public readonly showPremiumToggle$: Observable<boolean>;
  public readonly reviewAttributes$ = new Observable<ReviewAttribute[]>();

  public readonly profileSourceCategory = TrixelSourceCategories.AgentProfilePage;
  public readonly enquiryTypes = MortgageBrokerEnquiryTypes;
  public readonly contactTrixelSourceCategory = TrixelSourceCategories.AgentProfileContactForm;

  public constructor() {
    this.awards$ = this.awardsStore.awards$;
    this.badges$ = this.badgesStore.badges$;
    this.contactUrl$ = this.profileStore.contactUrl$;
    this.contactAnalytics$ = this.profileStore.contactAnalytics$;
    this.galleryAssets$ = this.galleryStore.galleryAssets$;
    this.hasTrial$ = this.premiumToggleStore.hasTrial$;
    this.premiumOverride$ = this.premiumToggleStore.premiumOverride$;
    this.profile$ = this.premiumToggleStore.profile$;
    this.downgrade$ = this.premiumToggleStore.downgrade$;
    this.reviews$ = this.reviewsStore.reviews$;
    this.share$ = this.profileStore.share$;
    this.showClaim$ = this.profileStore.showClaim$;
    this.showMapCta$ = this.reviews$.pipe(map((reviews) => reviews.total > 0));
    this.showPremiumToggle$ = this.premiumToggleStore.showPremiumToggle$;
    this.reviewAttributes$ = this.profileAttributeStore.reviewAttributes$;
  }

  public ngOnInit() {
    const metaSource$: Observable<ProfileMetaSource> = combineLatest({
      profile: this.profile$,
      reviews: this.reviews$.pipe(map(({ results }) => results)),
      auctions: of([]),
      links: of([]),
      awards: this.awards$,
    });
    this.profileMetaService.setMetaData(metaSource$);
  }

  public onPremiumToggle(state: boolean): void {
    this.premiumToggleStore.setToggleState(state);
  }
}
